/** @format */

import {
  AnalyticsService,
  BreakpointsService,
  DeviceService,
  EmitEvent,
  EventBusService,
  Events,
  QuickviewAvailabilityService,
  Scene7Service,
  Store,
  UtilityService,
  WINDOW,
} from '@shared/data-access/services';
import {
  BeaconQueryParameters,
  CertonaScheme,
  CertonaSchemeItem,
  Chain,
  CriteoProduct,
  CriteoTrackTransactionResponse,
  CriteoTrackingItem,
  CriteoViewItemMetadata,
  CriteoViewItemResponse,
  CriteoViewProductsResponse,
} from '@shared/data-access/interfaces';
import {
  CRITEO_CAROUSEL_MAXIMUM_PRODUCTS,
  CRITEO_CAROUSEL_MINIMUM_PRODUCTS,
  CriteoService,
  DmnAdService,
} from '@shared/data-access/product-recommendations';
import {
  CertonaRecommendations,
  CertonaRecommendationsResponse,
  CertonaService,
  RecommendationDataService,
  RecommendationScheme,
} from '@product-recommendations/data-access';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  OnInit,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  HmfButtonComponent,
  HmfCarouselComponent,
  HmfProductCardComponent,
  HmfSkeletonComponent,
} from '@dsg-tech/homefield-angular';
import { filter, map, take } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';
import { BreakPointNew } from '@shared/data-access/enums';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ProductRecsItemNamePipe } from '@shared/utils/pipes';

const EMPTY_CART_FILTER_KEY = 'cartempty_rr';

@Component({
  selector: 'shared-components-product-recommendations',
  templateUrl: './product-recommendations.component.html',
  styleUrl: './product-recommendations.component.scss',
  imports: [
    CommonModule,
    HmfButtonComponent,
    HmfCarouselComponent,
    HmfProductCardComponent,
    HmfSkeletonComponent,
    ProductRecsItemNamePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductRecommendationsComponent implements OnInit {
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW) as Window;
  private readonly productContainerService = inject(CertonaService);
  private readonly deviceService = inject(DeviceService);
  private readonly criteoService = inject(CriteoService);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly eventBusService = inject(EventBusService);
  private readonly store = inject(Store);
  private readonly ngZone = inject(NgZone);
  public readonly recommendationDataService = inject(RecommendationDataService);
  private readonly utilityService = inject(UtilityService);
  private readonly scene7Service = inject(Scene7Service);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chain = inject(Chain);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly quickviewAvailabilityService = inject(QuickviewAvailabilityService);

  readonly displayName = input<string>();
  readonly filterBy = input<string>();
  readonly schemes = input<CertonaScheme[]>();
  readonly showViewMoreLink = input<boolean>(false);
  readonly imageSize = input('');
  readonly isOOSRecommendations = input(false);
  readonly isOOSSimilarProducts = input(false);
  readonly isAlternativeInStock = input(false);
  readonly showQVButtons = input(false);
  readonly productRecommendationsQuickViewEnabled = input(false);
  readonly showHeader = input(true);
  readonly cardSize = input<HmfProductCardComponent['size']>();
  readonly isTabbedCarouselRecs = input(false);
  readonly showSkeletonLoading = input(false);
  readonly numberOfSkeletonCards = input(8);
  readonly isSidePanelRecs = input(false);
  readonly isSidePanelRecsRedesign = input(false);
  readonly showVertical = input(false);
  // Remove when all product info is in hmf-product-card
  readonly showVerticalLegacy = input(false);
  readonly useSignalRecommendations = input(false);
  readonly reserveLegacyCardImageHeight = input(false); // Can help with CLS in performance sensitive placements

  /**
   * @description
   * Needed if Criteo is used for sponsored products. Only used for PDP.
   */
  readonly criteoViewItemMetadata = input<CriteoViewItemMetadata>();

  /**
   * @description
   * Boolean flag representing if this component is being used on PLP.
   */
  readonly isPLPRecs = input(false);

  /**
   * @description
   * Boolean flag representing if this component is being used for cart OOS recs.
   */
  readonly isCartOOSRecs = input(false);

  /**
   * @description
   * Toggles sponsored recommendation visibility
   */
  readonly sponsored = input<boolean>();

  /**
   * @description
   * Ecode and catgroupId needed if sponsored is set to true
   */
  readonly ecode = input<string>();
  readonly pageType = input<string>();
  readonly homefieldCarouselEnabled = input(false);

  /**
   * @description
   * List of Criteo products used for PLP/SRLP or OCP sponsored products
   */
  readonly criteoProducts = input<CriteoViewProductsResponse | CriteoTrackTransactionResponse>();

  readonly useNewFormatQVRecs = input(false);
  readonly forceLazyLoadOOOImages = input(false);

  // Emit event when quick view is clicked
  readonly quickViewClick = output<string>();

  readonly schemas = signal<RecommendationScheme[]>([]);
  readonly productDisplayLimit = signal(6);
  readonly isPlaRecommendations = signal(false);
  readonly isMobile = signal(false);

  readonly atcCardText = computed(() =>
    this.cardSize() === 'small' || (this.filterBy() === EMPTY_CART_FILTER_KEY && this.isMobile())
      ? 'Add'
      : `${this.atcButtonTexts.atc}`,
  );

  readonly isVerticalGrid = computed(() => this.showVertical() || this.showVerticalLegacy());

  /**
   * Viewport-driven card size for the side-panel recommendations redesign
   * (`isSidePanelRecsRedesign`). Maps the current breakpoint to the designer's
   * card size tiers:
   * - small  : viewport < 480px  (XXS / XS)
   * - medium : 480px – 1439px    (S / M / L / XL)
   * - large  : viewport >= 1440px (XXL+)
   *
   * Uses `BreakpointsService` (SSR-safe) instead of `DeviceService.isMobile()`
   * so the size tracks the actual viewport width — including when a desktop
   * browser is narrowed below the medium tier.
   */
  readonly redesignCardSize = computed<HmfProductCardComponent['size']>(() => {
    const breakpoint = this.breakpointsService.breakpoint();
    if (breakpoint < BreakPointNew.SMALL) {
      return 'small';
    }
    if (breakpoint >= BreakPointNew.EXTRAEXTRALARGE) {
      return 'large';
    }
    return 'medium';
  });

  productRecommendations: CertonaRecommendationsResponse | null | undefined;

  /**
   * @description
   * Criteo tracking metadata for DSG analytics.
   */
  criteoTracking: CriteoTrackingItem[] = [];

  /**
   * @description
   * Criteo tracking metadata transformed for product card data-em attr.
   */
  readonly criteoDataEms = signal<string[]>([]);

  atcButtonTexts = ATC_BUTTON_TEXTS;
  /**
   * @description
   * Keeps track of which sponsored product impressions have already been called
   */
  sponsoredImpressionHistory: { [key: string]: boolean } = {};

  /*
	   Cart & Checkout doesn't use the ngRx Store, so we have to use the fallback method
	   for killswitches
	*/

  private readonly killSwitches = toSignal(
    this.store.selectDistinctKey('ENVIRONMENT_KILLSWITCH_VALUES'),
    {
      initialValue: null,
    },
  );

  readonly isUsingCriteoSPAAdServer = computed(() => {
    const killswitches = this.killSwitches();
    if (!killswitches) return false;

    return this.utilityService.checkKillSwitch(
      'isUsingCriteoSPAAdServer',
      killswitches,
      this.chain.chainIdentifierAbbr,
    );
  });

  readonly certonaQuickviewEnabled = computed(() => {
    const killswitches = this.killSwitches();
    if (!killswitches) return false;

    return this.utilityService.checkKillSwitch(
      'certonaQuickviewEnabled',
      killswitches,
      this.chain.chainIdentifierAbbr,
    );
  });

  readonly pdpDmnAdServerEnabled = computed(() => {
    const killswitches = this.killSwitches();
    if (!killswitches) return false;

    return this.utilityService.checkKillSwitch(
      'pdpDmnAdServerEnabled',
      killswitches,
      this.chain.chainIdentifierAbbr,
    );
  });

  private readonly SPONSORED_PRODUCTS_ID = 'sponsored-product';

  private readonly beaconQueryParamsLoad: BeaconQueryParameters = {
    placementType: 'CAROUSEL',
    placementLevel: 'PLACEMENT',
    beaconType: 'LOAD',
  };

  private readonly beaconQueryParamsPlacementView: BeaconQueryParameters = {
    placementType: 'CAROUSEL',
    placementLevel: 'PLACEMENT',
    beaconType: 'VIEW',
  };

  private readonly beaconQueryParamsProductView: BeaconQueryParameters = {
    placementType: 'CAROUSEL',
    placementLevel: 'PRODUCT',
    beaconType: 'VIEW',
  };

  private readonly beaconQueryParamsProductClick: BeaconQueryParameters = {
    placementType: 'CAROUSEL',
    placementLevel: 'PRODUCT',
    beaconType: 'CLICK',
  };

  private readonly computedSignalScheme = computed(() => {
    const filterBy = this.filterBy();
    if (this.useSignalRecommendations() && filterBy) {
      return this.productContainerService.getScheme(filterBy)();
    }
    return null;
  });

  private readonly signalScheme$ = toObservable(this.computedSignalScheme).pipe(
    filter(Boolean),
    map(
      (scheme): CertonaRecommendationsResponse => ({
        certonaRecommendations: {
          source: 'Monetate',
          resonance: { schemes: [scheme] },
        },
      }),
    ),
  );

  constructor() {
    /**
     * Reactive handler for the PLP/SRLP/OCP sponsored flow where criteoProducts is
     * passed in as an input. Runs whenever criteoProducts change ensuring
     * the carousel re-renders if the parent updates the input, after filtering or sorting.
     * The PDP flow is handled separately in enableCriteoOnPdp() via an API call.
     */
    effect(() => {
      const criteoProducts = this.criteoProducts();

      if (!isPlatformBrowser(this.platformId) || !criteoProducts) return;

      // Cancel any pending observer timers from the previous product set, then reset
      // impression history so new products can be tracked cleanly.
      if (this.utilityService.hasIntersectionObserverSupport()) {
        this.criteoService.clearObserverTimers();
      } else {
        // Set limit here to guard against effect running before ngOnInit
        this.productDisplayLimit.set(CRITEO_CAROUSEL_MAXIMUM_PRODUCTS);
      }

      this.sponsoredImpressionHistory = {};
      this.organizeCriteoProductsIntoSchema(criteoProducts);
    });
  }

  ngOnInit(): void {
    this.isMobile.set(this.deviceService.isMobile());

    if (this.showSkeletonLoading()) {
      this.setSchemeToEmptyForSkeletonLoading();
    }

    if (this.sponsored()) {
      this.isPlaRecommendations.set(true);
      if (this.isUsingCriteoSPAAdServer()) {
        this.productDisplayLimit.set(CRITEO_CAROUSEL_MAXIMUM_PRODUCTS);
        // PDP flow: make API call for ads. PLP/SRLP/OCP flow is handled reactively via criteoProducts()
        if (this.criteoViewItemMetadata()) {
          this.enableCriteoOnPdp();
        }
      }
    } else {
      if (this.filterBy() === 'pdplanding_rr') {
        this.isPlaRecommendations.set(true);
      }

      const certonaRecommendations$ = this.buildCertonaObservable();

      certonaRecommendations$
        .pipe(take(4), takeUntilDestroyed(this.destroyRef))
        .subscribe((recommendations) => {
          if (this.filterBy() !== EMPTY_CART_FILTER_KEY) {
            this.productRecommendations = recommendations;
          }
          if (isPlatformBrowser(this.platformId) || this.isPlaRecommendations()) {
            // Parse the product recommendations;
            this.parseCertonaRecommendations(recommendations);
          }
          if (recommendations && recommendations.certonaRecommendations.source !== 'skeleton') {
            this.callBttApis('end');
          }
        });
    }

    if (!this.isAlternativeInStock() && this.isOOSRecommendations()) {
      this.fireAnalyticsEventForOOSRec();
    }
    this.callBttApis('start');
  }

  private buildCertonaObservable() {
    if (this.useSignalRecommendations()) {
      return this.signalScheme$;
    }

    // Listen for product recommendations from Certona / Monetate.
    // Don't emit when recommendations don't include filtered schema, e.g. when skeleton is created for another schema
    // ...except when the recs come from Monetate, which sometimes doesn't include a schema if it's empty.
    // That gets handled in parseCertonaRecommendations().
    return this.filterBy()
      ? this.productContainerService.certonaRecommendationsState.pipe(
          filter(
            (res) =>
              !!res?.certonaRecommendations?.resonance?.schemes?.find(
                (s) => s.scheme === this.filterBy(),
              ) || res?.certonaRecommendations?.source === 'Monetate',
          ),
        )
      : this.productContainerService.certonaRecommendationsState;
  }

  enableCriteoOnPdp(): void {
    const criteoViewItemMetadata = this.criteoViewItemMetadata();
    if (!isPlatformBrowser(this.platformId) || !criteoViewItemMetadata) {
      return;
    }

    const { adServiceCall, beaconFn } = this.pdpDmnAdServerEnabled()
      ? {
          adServiceCall: (metadata: CriteoViewItemMetadata) =>
            this.dmnAdService.getPdpAds(metadata),
          beaconFn: (beacon: string, excluded?: string) =>
            this.dmnAdService.sendBeacon(beacon, this.beaconQueryParamsLoad, excluded),
        }
      : {
          adServiceCall: (metadata: CriteoViewItemMetadata) =>
            this.criteoService.viewItem(metadata),
          beaconFn: (beacon: string, excluded?: string) =>
            this.criteoService.fireBeacon(beacon, excluded),
        };

    adServiceCall(criteoViewItemMetadata)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const carousel = res?.placements?.[0]?.carousel?.[0];
          if ((carousel?.products?.length ?? 0) < CRITEO_CAROUSEL_MINIMUM_PRODUCTS) {
            this.schemas.set([
              this.setRecommendationScheme({
                items: [],
                display: 'no',
                explanation: '',
                scheme: '',
              }),
            ]);
          } else {
            if (carousel?.OnLoadBeacon) {
              const excluded = this.dmnAdService.buildExcludedCriteoProductsParam(
                carousel.products,
              );
              beaconFn(carousel.OnLoadBeacon, excluded);
            }
            this.organizeCriteoProductsIntoSchema(res);
          }
        },
      });
  }

  /**
   * @description
   * Transforms a Criteo API response into the internal `CertonaScheme` format and
   * updates `schemas`. Slices products to `CRITEO_CAROUSEL_MAXIMUM_PRODUCTS` to
   * prevent rendering more cards than the display limit. Sets `display: 'no'` when
   * there are no displayable products so the carousel container is removed from the
   * DOM. Also registers IntersectionObserver listeners for OnView
   * beacon firing after the carousel renders.
   *
   * Note: the OnLoad beacon is **not** fired here — it is fired upstream before
   * this method is called (in `enableCriteoOnPdp` for PDP, or in the NgRx effect /
   * confirmation-container handler for PLP/SRLP/OCP).
   */
  organizeCriteoProductsIntoSchema(
    res: CriteoViewItemResponse | CriteoViewProductsResponse | CriteoTrackTransactionResponse,
  ): void {
    const schema: CertonaScheme = {
      items: [],
      display: 'yes',
      explanation: '',
      scheme: '',
    };
    const carousel = Array.isArray(res.placements)
      ? res.placements?.[0]?.carousel?.[0]
      : res.placements?.carousel?.[0];
    const products = carousel?.products;
    const displayableProducts = products?.slice(0, CRITEO_CAROUSEL_MAXIMUM_PRODUCTS) ?? [];
    if (displayableProducts.length > 0) {
      schema.items = this.criteoService.transformCriteoCarouselProducts(displayableProducts);
      this.setCriteoTracking(displayableProducts);
      // Create events for when the carousel and items scroll into view that will fire the appropriate Beacons
      setTimeout(() => {
        this.createIntersectionObservers(carousel.OnViewBeacon);
      }, 10);
    }

    const transformedSchema = this.setRecommendationScheme(schema);
    this.schemas.set([transformedSchema]);
  }

  parseCertonaRecommendations(response: CertonaRecommendationsResponse | null | undefined): void {
    // Check to ensure there is a valid response to parse
    if (!response?.certonaRecommendations?.resonance?.schemes) return;

    const schemas = response.certonaRecommendations.resonance.schemes.filter(
      (schema) => schema.scheme === this.filterBy(),
    );

    if (schemas.length === 0 && response.certonaRecommendations.source === 'Monetate') {
      // If Monetate recs didn't include this scheme, treat it as if it included an empty one.
      // This is because Monetate sometimes leaves out empty schemas altogether,
      // instead of sending empty ones with `display: "no"` like Certona does.
      schemas.push({
        display: 'no',
        explanation: '',
        items: [],
        scheme: this.filterBy() ?? '',
      });
    }

    const transformedSchemas = schemas.map((schema) => {
      if (this.filterBy() === 'oosproduct_rr') {
        this.productDisplayLimit.set(3);
      } else {
        this.productDisplayLimit.set(schema.items.length);
      }

      return this.setRecommendationScheme(schema);
    });

    // Check to see if there are any schemas to display
    if (transformedSchemas.length > 0) {
      this.schemas.set(transformedSchemas);
    }
  }

  /**
   * @description
   * Used to determine if a sponsored recommendation card is within view
   * If a card is within view we will trigger the Criteo Beacon
   */
  createIntersectionObservers(criteoOnViewBeacon: string) {
    // Intersection Observer for analytics impression tracker
    // Anaytics impression tracker needs to know when sponsored recs container is in view

    const sponsoredProductsContainer = document.getElementById(this.SPONSORED_PRODUCTS_ID);
    if (!sponsoredProductsContainer) {
      return;
    }

    // Placement level (carousel) OnViewBeacon call to Criteo
    this.createCriteoPlacementObserve(sponsoredProductsContainer, criteoOnViewBeacon);
  }

  createCriteoPlacementObserve(container: HTMLElement, beacon: string): void {
    // Guard against null container from getElementById
    if (!container) {
      return;
    }
    if (this.sponsoredImpressionHistory[this.SPONSORED_PRODUCTS_ID] === false) {
      // This needs to be explicitly false in order to work properly
      return;
    }
    this.sponsoredImpressionHistory[this.SPONSORED_PRODUCTS_ID] = false;
    const criteoCarouselCallBack = () => {
      this.dmnAdService.sendBeacon(beacon, this.beaconQueryParamsPlacementView);
      this.sponsoredImpressionHistory[this.SPONSORED_PRODUCTS_ID] = true;
    };
    this.criteoService.sponsoredProductsIntersectionObserver(container, criteoCarouselCallBack);

    for (let i = 0, length = this.schemas()[0].items.length; i < length; ++i) {
      const item = this.schemas()[0].items[i];
      if (!item) {
        return;
      }
      const sponsoredProductCard = container.querySelector(`#spa-carousel-${item.ID}`);
      if (sponsoredProductCard instanceof HTMLElement) {
        this.createCriteoProductObserve(item, sponsoredProductCard, i);
      }
    }
  }

  createCriteoProductObserve(
    item: CertonaSchemeItem,
    productCard: HTMLElement,
    index: number,
  ): void {
    const productOnViewBeacon = item.impression_tracker;
    if (!productOnViewBeacon) {
      return;
    }

    const criteoCallback = () => {
      this.dmnAdService.sendBeacon(productOnViewBeacon, this.beaconQueryParamsProductView);
      const onViewTrackingItem = this.criteoTracking[index];
      this.analyticsService.triggerAnalyticsEvent('contentImpression', onViewTrackingItem);
    };

    this.criteoService.sponsoredProductsIntersectionObserver(productCard, criteoCallback);
  }

  private setCriteoTracking(displayableProducts: CriteoProduct[]): void {
    const experience = globalThis._dsgTag?.experience ?? 'Desktop';
    const eventType = this.getCriteoEventType();
    this.criteoTracking = [];
    const dataEms: string[] = [];
    displayableProducts.forEach((product, index) => {
      const trackingItem = {
        vendor: 'Criteo',
        lineItemId: product.LineItemID,
        placements: `${eventType}_API_${experience}_Carousel`,
        slot: index + 1,
      };
      this.criteoTracking.push(trackingItem);
      this.analyticsService.triggerAnalyticsEvent('contentRendered', trackingItem);
      dataEms.push(
        `Criteo:${trackingItem.lineItemId}:${trackingItem.placements}:${trackingItem.slot}`,
      );
    });
    this.criteoDataEms.set(dataEms);
  }

  private getCriteoEventType(): string {
    switch (globalThis._dsgTag?.pageType?.Name) {
      case 'Family':
        return 'viewCategory';
      case 'Search':
        return 'viewSearchResult';
      case 'Product Detail':
        return 'viewItem';
      case 'Order Confirmation':
        return 'trackTransaction';
      default:
        return 'unknownPageType';
    }
  }

  /**
   * @description
   * Tranforms schema pricing to be consumable by Angular currency pipe in the HTML,
   * sets image params, and sets schema.certonaDisplayText based on schema.explanation
   * or displayName variable
   */
  setRecommendationScheme(schema: CertonaScheme): RecommendationScheme {
    const items = schema.items.map((item): CertonaSchemeItem | null =>
      item
        ? {
            ...item,
            maxmapprice: item.maxmapprice?.replace(',', ''),
            maxofferprice: item.maxofferprice?.replace(',', ''),
            minmapprice: item.minmapprice?.replace(',', ''),
            minofferprice: item.minofferprice?.replace(',', ''),
            offerprice: item.offerprice?.replace(',', ''),
            mapPriceIndicator:
              item.mapPriceIndicator ?? (item?.breakingMap || item?.BreakingMap === 'Y' ? 1 : 0),
            ImageURL: this.scene7Service.setImageWidthAndHeight(item.ImageURL, 500, 500),
          }
        : item,
    );

    const displayName = this.displayName();
    let certonaDisplayText: string | undefined = undefined;
    // Check to see if a display text has been provided
    if (typeof schema.explanation === 'string' && schema.explanation.trim() !== '') {
      certonaDisplayText = schema.explanation;
    } else if (typeof displayName === 'string' && displayName.trim() !== '') {
      certonaDisplayText = displayName;
    }

    // Set display='no' for schemas with 0 items
    // This prevents empty schemas from displaying while still keeping them in the array
    // so that skeleton loaders can be hidden immediately
    const display = items.length > 0 ? schema.display : 'no';

    return {
      ...schema,
      items,
      certonaDisplayText,
      cardSize: this.getProductCardSize(certonaDisplayText, this.isMobile()),
      display,
    };
  }

  /**
   * @description
   * Determines the size of the product card based on the certona display text and if the user is on mobile
   * @param certonaDisplayText - The display text provided by certona
   * @param isMobile - Boolean flag representing if the user is on mobile
   * @returns - The size of the product card
   */
  getProductCardSize(
    certonaDisplayText: string | undefined,
    isMobile: boolean,
  ): 'small' | 'medium' {
    if (certonaDisplayText === 'Recently Viewed' && isMobile) {
      return 'small';
    }
    if (
      certonaDisplayText === 'Similar Products to Consider' ||
      certonaDisplayText === 'Try These Instead'
    ) {
      return 'medium';
    }

    return isMobile ? 'small' : 'medium';
  }

  fireAnalyticsEventForOOSRec(): void {
    this.analyticsService.triggerAnalyticsEvent('OOSRecProduct');
  }

  openQuickviewPanel(ecode: string, url: string, sku: string | null = null): void {
    // Get recid query param from URL
    const qvRecIDToken = this.recommendationDataService.getAnalyticsDataFromUrl(url);
    const dataAndCallBack: Record<string, unknown> = {
      additionalData: this.useNewFormatQVRecs() ? qvRecIDToken : qvRecIDToken.recid,
      callback: this.onQuickViewAddToCartSuccess.bind(this),
    };

    const referringPage = this.useNewFormatQVRecs() ? 'Monetate' : 'certona';

    if (sku) {
      dataAndCallBack['skuPartNumber'] = sku;
    }

    this.quickViewClick.emit(ecode);

    this.quickviewAvailabilityService.queueUntilAvailable((qv) => {
      qv.openProductQuickView(ecode, referringPage, dataAndCallBack);
    });
  }

  /**
   * @description
   * Blue Triangle tagging for certona containers.
   * start() called on page load.
   * end() called when response from api recieved and component rendered.
   */
  callBttApis(apiName: 'start' | 'end'): void {
    if (
      isPlatformBrowser(this.platformId) &&
      (this.pageType() === 'plp' || this.pageType() === 'srlp') &&
      this.filterBy() &&
      this.window['bttUT']
    ) {
      this.window['bttUT'][apiName]({
        pageName: `angular ${this.pageType()} - certona`,
        txnName: 'eCommerce',
      });
    }
  }

  /**
   * @description Event handler for when a homefield card is clicked
   * @param item - The CertonaSchemeItem that was clicked
   */
  handleProductCardClicked(item: CertonaSchemeItem): void {
    if (!item) return;

    if (this.productRecommendationsQuickViewEnabled()) {
      if (item.SKUID) {
        this.openQuickviewPanel(item.ID, item.URL, item.SKUID);
      } else {
        this.openQuickviewPanel(item.ID, item.URL);
      }
    } else {
      this.goToProductUrl(item);
    }
  }

  handleATCBtnClick(item) {
    if (item.SKUID) {
      this.openQuickviewPanel(item?.ID, item?.URL, item.SKUID);
    } else {
      this.openQuickviewPanel(item?.ID, item?.URL);
    }
  }

  goToProductUrl(item: CertonaSchemeItem): void {
    if (this.sponsored() && item.click_tracker) {
      this.dmnAdService.sendBeacon(item.click_tracker, this.beaconQueryParamsProductClick);
    }
    this.window.location = item.URL;
  }

  trackByCertona(index: number, item: CertonaSchemeItem): string {
    if (!item) return index.toString();
    return index.toString() + item.ID + (item.SKUID ? item.SKUID : '');
  }

  trackBySchema(index: number, item: CertonaSchemeItem): string {
    return index.toString() + (item ? item.ID + item.WCSIdentifier : '');
  }

  /**
   * @description Set the schema to empty for skeleton loading
   * This will ensure that the product recommendations are not displayed until new data is received
   */
  setSchemeToEmptyForSkeletonLoading(): void {
    // Set recs to empty at the service level to ensure nothing shows until we get new data
    const emptyRecs: CertonaRecommendations = {
      resonance: {
        schemes: [
          {
            scheme: this.filterBy() || 'skeleton',
            explanation: '',
            display: 'yes',
            items: new Array(this.numberOfSkeletonCards()).fill(null),
          },
        ],
      },
      source: 'skeleton',
    };

    this.productContainerService.recommendations(emptyRecs);

    // If skeleton loaders still showing after 10 seconds, remove them
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        // Timeouts should only be in client only
        setTimeout(() => {
          if (this.productRecommendations?.certonaRecommendations.source === 'skeleton') {
            this.ngZone.run(() => {
              this.productContainerService.recommendations({
                resonance: {
                  schemes: [
                    {
                      scheme: this.filterBy() || 'skeleton',
                      explanation: '',
                      display: 'no',
                      items: [],
                    },
                  ],
                },
                source: 'skeleton',
              });
            });
          }
        }, DCSG_CONSTANTS.productRecs.timeout);
      });
    }
  }

  /**
   * @description
   * Event handler for the QuickView "ATC Success Event".  It will emit an Event
   * on the Event Bus to be picked up by any listeners for additional processing.
   */
  private onQuickViewAddToCartSuccess() {
    this.eventBusService.emit(new EmitEvent(Events.PostATCEvent));
  }

  goToRecommendedForYou(): void {
    // Go to Recommended For You page /s/recommended-for-you
    this.window.location.href = '/s/recommended-for-you';
  }

  productHasDiscount(item: CertonaSchemeItem): boolean {
    const minOfferPrice = this.recommendationDataService.comparablePrice(item.minofferprice);
    const maxOfferPrice = this.recommendationDataService.comparablePrice(item.maxofferprice);
    const minListPrice = this.recommendationDataService.comparablePrice(item.minlistprice);
    const maxListPrice = this.recommendationDataService.comparablePrice(item.maxlistprice);

    return (maxOfferPrice < maxListPrice || minOfferPrice < minListPrice) && this.isVerticalGrid();
  }
}
